import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, Loader2, ArrowRight } from "lucide-react";

export default function Setup() {
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", password: "",
    university_name: "", short_name: "", university_url: "",
    primary_color: "#1e3a5f", accent_color: "#c08a2d",
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.post("/setup", f);
      toast.success("Instance ready — sign in with your new account");
      setTimeout(() => { window.location.href = "/login"; }, 900);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Setup failed");
      setSaving(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none";

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="setup-page">
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-3 mb-6 text-white">
          <div className="h-12 w-12 rounded-xl bg-indigo-500 flex items-center justify-center shrink-0">
            <GraduationCap className="h-6 w-6" />
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-indigo-300">AlumniOS · First-run setup</div>
            <h1 className="font-serif text-2xl leading-none mt-1">Set up your Alumni Hub</h1>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl p-7 space-y-6">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mb-3">Owner account</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700">Your full name</label>
                <input data-testid="setup-name" value={f.name} onChange={set("name")} required className={inputCls} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <input data-testid="setup-email" type="email" value={f.email} onChange={set("email")} required className={inputCls} placeholder="you@university.edu" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Password</label>
                <input data-testid="setup-password" type="password" value={f.password} onChange={set("password")} required minLength={8} className={inputCls} placeholder="At least 8 characters" />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <div className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mb-3">University</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">University name</label>
                <input data-testid="setup-university-name" value={f.university_name} onChange={set("university_name")} required className={inputCls} placeholder="State University" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Short name <span className="text-slate-400 font-normal">(optional)</span></label>
                <input data-testid="setup-short-name" value={f.short_name} onChange={set("short_name")} className={inputCls} placeholder="SU Alumni" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700">Website <span className="text-slate-400 font-normal">(optional)</span></label>
                <input data-testid="setup-url" value={f.university_url} onChange={set("university_url")} className={inputCls} placeholder="https://university.edu" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Primary color</label>
                <div className="mt-1 flex items-center gap-2">
                  <input data-testid="setup-primary-color" type="color" value={f.primary_color} onChange={set("primary_color")} className="h-10 w-14 rounded border border-slate-300 cursor-pointer" />
                  <input value={f.primary_color} onChange={set("primary_color")} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Accent color</label>
                <div className="mt-1 flex items-center gap-2">
                  <input data-testid="setup-accent-color" type="color" value={f.accent_color} onChange={set("accent_color")} className="h-10 w-14 rounded border border-slate-300 cursor-pointer" />
                  <input value={f.accent_color} onChange={set("accent_color")} className={inputCls} />
                </div>
              </div>
            </div>
          </div>

          <button data-testid="setup-submit" type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : <>Create instance <ArrowRight className="h-4 w-4" /></>}
          </button>
          <p className="text-xs text-slate-400 text-center">You can refine branding, logos and background images later from the Branding page.</p>
        </form>
      </div>
    </div>
  );
}
